
const BV_BASE = '__BV_toggle'
const BV_TOGGLE_ROOT_HANDLER = `${BV_BASE}_HANDLER__`
const BV_TOGGLE_CLICK_HANDLER = `${BV_BASE}_CLICK__`
const BV_TOGGLE_STATE = `${BV_BASE}_STATE__`
const BV_TOGGLE_TARGETS = `${BV_BASE}_TARGETS__`
const ROOT_ACTION_EVENT_NAME_TOGGLE = 'bv::toggle::collapse'
const ROOT_ACTION_EVENT_NAME_REQUEST_STATE = 'bv::request-state::collapse'
const ROOT_EVENT_NAME_STATE = 'bv::collapse::state'
const ROOT_EVENT_NAME_SYNC_STATE = 'bv::collapse::sync-state'
const KEYDOWN_KEY_CODES = [13, 32]

const eventRoot = document
const isLinkOrButton = el => ['button', 'a'].includes(el.tagName.toLowerCase())
const isDisabled = el => el.disabled || el.hasAttribute('disabled') || el.classList.contains('disabled')

const setToggleState = (el, state) => {
  el.classList.remove(state ? 'collapsed' : 'not-collapsed')
  el.classList.add(state ? 'not-collapsed' : 'collapsed')
  el.setAttribute('aria-expanded', state ? 'true' : 'false')
}

const removeRootListeners = (el, instance) => {
  if (el[BV_TOGGLE_ROOT_HANDLER] && instance) {
    eventRoot.removeEventListener(ROOT_EVENT_NAME_STATE, el[BV_TOGGLE_ROOT_HANDLER])
    eventRoot.removeEventListener(ROOT_EVENT_NAME_SYNC_STATE, el[BV_TOGGLE_ROOT_HANDLER])
  }
  el[BV_TOGGLE_ROOT_HANDLER] = null
}

const addRootListeners = (el, instance) => {
  removeRootListeners(el, instance)
  if (instance) {
    const handler = (id, state) => {
      if ((el[BV_TOGGLE_TARGETS] || []).includes(id)) {
        el[BV_TOGGLE_STATE] = state
        setToggleState(el, state)
      }
    }
    el[BV_TOGGLE_ROOT_HANDLER] = handler
    eventRoot.addEventListener(ROOT_EVENT_NAME_STATE, handler)
    eventRoot.addEventListener(ROOT_EVENT_NAME_SYNC_STATE, handler)
  }
}

const removeClickListener = el => {
  const handler = el[BV_TOGGLE_CLICK_HANDLER]
  if (handler) {
    el.removeEventListener('click', handler, { passive: true })
    el.removeEventListener('keydown', handler, { passive: true })

  }
  el[BV_TOGGLE_CLICK_HANDLER] = null
}

const addClickListener = (el, instance) => {
  removeClickListener(el)
  if (instance) {
    const handler = event => {
      if (
        !(event.type === 'keydown' && !KEYDOWN_KEY_CODES.includes(event.keyCode)) && !isDisabled(el)
      ) {
        const targets = el[BV_TOGGLE_TARGETS] || []
        targets.forEach(target => {
          eventRoot.dispatchEvent(new CustomEvent(ROOT_ACTION_EVENT_NAME_TOGGLE, { detail: target }))
        })
      }
    }
    el[BV_TOGGLE_CLICK_HANDLER] = handler
    el.addEventListener('click', handler, { passive: true })
    if (!isLinkOrButton(el)) {
      el.addEventListener('keydown', handler, { passive: true })
    }
  }
}

const getTargets = ({ modifiers, arg, value }, el) => {
  const targets = Object.keys(modifiers || {})

  value = typeof value === 'string' ? value.split(/\s+/) : value

  // Support target ID as link href (`href="#id"`)
  if (el.tagName === 'a') {
    const RX_HASH_ID = /^#[A-Za-z]+[\w\-:.]*$/
    const RX_HASH = /^#/
    const href = el.getAttribute('href') || ''
    if (RX_HASH_ID.test(href)) {
      targets.push(href.replace(RX_HASH, ''))
    }
  }
  const concat = (...args) => Array.prototype.concat.apply([], args)
  concat(arg, value).forEach(t => typeof t === 'string' && targets.push(t))

  return targets.filter((t, index, arr) => t && arr.indexOf(t) === index)
}

const handleUpdate = (el, binding) => {
  // If element is not a button or link, we add `role="button"`
  // and `tabindex="0"` for accessibility reasons
  if (!isLinkOrButton(el)) {
    if (!el.hasAttribute('role')) {
      el.setAttribute('role', 'button')
    }
    if (!el.hasAttribute('tabindex')) {
      el.setAttribute('tabindex', '0')
    }
  }

  // Ensure the collapse class and `aria-*` attributes persist
  // after element is updated (either by parent re-rendering
  // or changes to this element or its contents)
  setToggleState(el, el[BV_TOGGLE_STATE])

  const targets = getTargets(binding, el)

  if (targets.length > 0) {
    el.setAttribute('aria-controls', targets.join(' '))
    el.style['overflow-anchor'] = 'none'
  } else {
    el.removeAttribute('aria-controls')
    el.style['overflow-anchor'] = ''
  }
  requestAnimationFrame(() => {
    addClickListener(el, binding.instance)
  })

  if (!targets.every((val, i) => val === el[BV_TOGGLE_TARGETS][i])) {
    el[BV_TOGGLE_TARGETS] = targets
    targets.forEach(target => {
      eventRoot.dispatchEvent(new CustomEvent(ROOT_ACTION_EVENT_NAME_REQUEST_STATE, { detail: target }))
    })
  }
}

const VBToggle = {
  beforeMount(el, binding) {
    el[BV_TOGGLE_STATE] = false
    el[BV_TOGGLE_TARGETS] = []
    addRootListeners(el, binding.instance)
    handleUpdate(el, binding)
  },
  componentUpdated: handleUpdate,
  updated: handleUpdate,
  unmounted(el, binding) {
    const resetProp = (prop) => {
      el[prop] = null
      delete el[prop]
    }
    handleUpdate(el, binding)
    removeClickListener(el)
    removeRootListeners(el, binding.instance)
    resetProp(BV_TOGGLE_ROOT_HANDLER)
    resetProp(BV_TOGGLE_CLICK_HANDLER)
    resetProp(BV_TOGGLE_STATE)
    resetProp(BV_TOGGLE_TARGETS)
    el.classList.remove('collapsed', 'not-collapsed')
    el.removeAttribute('aria-expanded')
    el.removeAttribute('aria-controls')
    el.removeAttribute('role')
    el.style['overflow-anchor'] = ''
  }
}

export default VBToggle
